import Vue from 'vue';
import Vuex from 'vuex';
import Cookies from 'js-cookie';
import {
    getProfileKey,
    createLeadgenVuexPersistence,
    createSsnSessionPlugin,
} from './persistence';

Vue.use(Vuex);

// Tab-scoped SSN persistence. SSN is stripped from the localStorage snapshot for
// PCI; this plugin keeps it alive across reloads within the same tab only.
const vuexSsnSessionPlugin = createSsnSessionPlugin({ keyBase: 'leadgen_v2_ssn' });

// Per-profile localStorage persistence with expiry + PCI sanitization.
const vuexLocal = createLeadgenVuexPersistence({ key: 'leadgen_v2_form' });

const BASE_STATE = {
    impressionTimeoutId: null,
    signup: 'original',
    amountByState: window.app.amount_by_state,
    vpnDetected: false,
    account: {
        id: null,
        external_id: null,
        r: null,
        referred_by: null,
        email: window.KlaviyoProfile?.email || window.userMarketingProfile?.email || '',
        password: '',
        accurate_check: false,
        bankruptcy_check: false,
        adult_check: false,
        electronic_consent_check: false,
        agree_marketing_sms: false,
        agree_account_sms: false,
        service_check: false,
        mode: 'v3'
    },
    contact: {
        verified: false,
        first_name: window.KlaviyoProfile?.first_name || window.userMarketingProfile?.first_name || '',
        last_name: window.KlaviyoProfile?.last_name || window.userMarketingProfile?.last_name || '',
        mail_campaing_user_token: '',
        ssn: window.KlaviyoProfile?.formatted_ssn || '',
        date_of_birth: '',
        birth_day: null,
        birth_month: null,
        birth_year: null,
        contact_email: window.KlaviyoProfile?.email || '',
        email: window.KlaviyoProfile?.email || '',
        phone_number: '',
        phone_number_type: 'cellphone',
        address: '',
        address2: '',
        state: null,
        city: '',
        zip: '',
        housing: '',
        months_at_residence: '',
        pay_period: null,
        next_pay_date: null,
        first_payment_day: null,
        second_payment_day: null,
        payment_day_of_the_week: null,
        monthly_income: '',
        income_source: null,
        employer: '',
        work_phone: '',
        months_employed: '',
        drivers_license_number: '',
        drivers_license_state: null,
        job_title: ''
    },
    loan: {
        amount: '500',
        period: '6:months',
        first_payment_date: null,
        timezone: null,
        template_id: null
    },
    bank: { // used just to leadgen signup
        official_name: '',
        routing_number: '',
        routing_number_confirmation: '',
        account_number: '',
        account_number_confirmation: '',
    },
    errors: {},
    generalErrors: {}
};

export default new Vuex.Store({
    state: BASE_STATE,
    getters: {
        signup: state => state.signup,
        account: state => state.account,
        contact: state => state.contact,
        errors: state => state.errors,
        generalErrors: state => state.generalErrors,
        loan: state => state.loan,
        amountByState: state => state.amountByState,
        vpnDetected: state => state.vpnDetected,

        getAmountByState: (state) => (abbrState = 'default') => {
            if(state.amountByState.hasOwnProperty(abbrState)) {
                return state.amountByState[abbrState];
            }

            return state.amountByState.default;
        }
    },
    mutations: {
        /**
         * Resets given key or entire store...
         *
         * @param {Object} state
         * @param {String} key
         * @returns void
         */
        reset(state, key = null) {
            if (key) {
                Vue.set(state, key, BASE_STATE[key]);
            } else {
                Object.keys(state).forEach(key => {
                    Vue.set(state, key, BASE_STATE[key]);
                });
            }
        },

        /**
         * Updates nested state property "loan"...
         *
         * @param {Object} state
         * @param {String} key
         * @param {String} value
         * @returns void
         */
        updateLoan(state, { key, value }) {
            state.loan[key] = value;
        },

        /**
         * Updates nested state property "contact"...
         *
         * @param {Object} state
         * @param {String} key
         * @param {String} value
         * @returns void
         */
        updateContact(state, { key, value }) {
            state.contact[key] = value;
        },

        /**
         * Updates general errors for display in error banner
         *
         * @param {Object} state
         * @param {Object} errors
         * @returns void
         */
        updateGeneralErrors(state, errors) {
            state.generalErrors = errors || {};
        },

        /**
         * Clears general errors
         *
         * @param {Object} state
         * @returns void
         */
        clearGeneralErrors(state) {
            state.generalErrors = {};
        },

        /**
         * Updates nested state property "contact"...
         * @param {Object} state
         * @param {Object} values
         * @returns void
        */
        updateContactAtOnce(state, values) {
            // TODO: This is a temporary solution to handle fields that are not present in the form.
            state.contact.drivers_license_number = 'blank';
            state.contact.drivers_license_state = 'blank';
            state.contact.next_pay_date = new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];

            state.contact = { ...state.contact, ...values };
        },

        /**
         * Updates nested state property "account"...
         *
         * @param {Object} state
         * @param {String} key
         * @param {String} value
         * @returns void
         */
        updateAccount(state, { key, value }) {
            state.account[key] = value;
        },

        updateBank(state, { key, value }) {
            state.bank[key] = value;
        },

        /**
         * Sets the "errors" property on the state...
         *
         * @param {Object} state
         * @param {Object} errors
         * @returns void
         */
        setErrors(state, errors) {
            state.errors = errors;
        },

        /**
         * Updates nested state property "errors"...
         *
         * @param {Object} state
         * @param {Array} errors
         * @returns void
         */
        updateErrors(state, errors) {
            Vue.set(state, 'errors', errors);
        },

        setSignup(state, signup) {
            state.signup = signup;
        },

        /**
         * @param state
         * @param timeoutId
         */
        updateImpressionTimeoutId(state, timeoutId) {
            state.impressionTimeoutId = timeoutId;
        },

        /**
         * @param state
         */
        clearImpressionTimeoutId(state) {
            state.impressionTimeoutId = null;
        },

        /**
         * Sets the VPN detection state
         *
         * @param {Object} state
         * @param {Boolean} detected
         * @returns void
         */
        setVpnDetected(state, detected) {
            state.vpnDetected = detected;
        },

        /**
         * Clear persisted form data from localStorage.
         *
         * Resets the live state back to BASE_STATE first: vuex-persist's
         * save-on-every-mutation subscriber fires for THIS mutation too, so if
         * `state` were left untouched, the still-fully-populated in-memory
         * state would be immediately re-persisted right after the removeItem
         * call below, silently undoing the clear (#5752 follow-up — a
         * resubmitted/re-entered lead was landing on the SSN step instead of
         * the start because the "cleared" form data was actually still there).
         *
         * @param {Object} state
         * @returns void
         */
        clearPersistedFormData(state) {
            Object.keys(state).forEach((key) => {
                Vue.set(state, key, BASE_STATE[key]);
            });

            try {
                const profileKey = getProfileKey();
                const storageKey = `leadgen_v2_form_${profileKey}`;
                localStorage.removeItem(storageKey);
                // Also clear from cookies
                Cookies.remove('leadgen_v2_form');
                console.log('[Vuex] Persisted form data cleared');
            } catch (error) {
                console.warn('[Vuex] Error clearing persisted form data:', error);
            }
        }
    },
    actions: {
        /**
         * @param state
         * @param commit
         * @param dispatch
         * @param payload
         */
        updateContact({ state, commit, dispatch }, payload) {
            commit('updateContact', payload);

            if (state.impressionTimeoutId) {
                clearTimeout(state.impressionTimeoutId);

                commit('clearImpressionTimeoutId');
            }

            let impressionTimeoutId = setTimeout(() => {
                dispatch('saveImpression');

                commit('clearImpressionTimeoutId');
            }, 5000);

            commit('updateImpressionTimeoutId', impressionTimeoutId);
        },

        updateContactAtOnce({ state, commit, dispatch }, payload) {
            commit('updateContactAtOnce', payload);

            if (state.impressionTimeoutId) {
                clearTimeout(state.impressionTimeoutId);

                commit('clearImpressionTimeoutId');
            }

            let impressionTimeoutId = setTimeout(() => {
                dispatch('saveImpression');

                commit('clearImpressionTimeoutId');
            }, 5000);

            commit('updateImpressionTimeoutId', impressionTimeoutId);
        },

        /**
         * @param commit
         * @returns {Promise<unknown>}
         */
        resetToBaseState({ commit }) {
            return new Promise((resolve, reject) => {
                commit('reset');

                resolve();
            });
        },

        /**
         * @param commit
         * @returns {Promise<unknown>}
         */
        resetErrorsToBaseState({ commit }) {
            return new Promise((resolve, reject) => {
                commit('reset', 'errors');

                resolve();
            });
        },

        /**
         * @param store
         * @param commit
         * @param state
         * @returns {Promise<unknown>}
         */
        saveImpression({ store, commit, state }) {
            return new Promise((resolve, reject) => {
                window.grecaptcha.ready(() => {
                    window.grecaptcha.execute(window.recaptchaV3Sitekey, { action: `${window.app.id}Impression` })
                        .then(token => {
                            if (token) {
                                return window.Vue.$http.post(`/signup/save-impression`, {
                                    recaptcha: token,
                                    applicationData: state
                                }).then(resolve).catch();
                            }
                        });
                });
            })
        },

        /**
         * @param commit
         * @param state
         * @param dispatch
         * @returns {Promise<unknown>}
         */
        validateContactInformation({ commit, state, dispatch }) {
            dispatch('saveImpression');

            return new Promise((resolve, reject) => {
                return window.Vue.$http.post(`/signup/contact-information/validate`, {
                    contact: state.contact
                }).then(resolve).catch(error => {
                    commit('updateErrors', error.response.data.errors);
                });
            });
        },

        /**
         * @param commit
         * @param state
         * @param dispatch
         * @returns {Promise<unknown>}
         */
        verifyIdentity({ commit, state, dispatch }) {
            dispatch('saveImpression');
            commit('reset', 'errors');

            return new Promise((resolve, reject) => {                
                return window.Vue.$http.post(`/accounts/verify-identity`, {
                    contact: state.contact
                }).then(({ data }) => {
                    commit('updateContact', { key: 'verified', value: true })

                    resolve(data);
                }).catch(error => {
                    commit('updateErrors', error.response.data.errors);
                    reject(error);
                });
            })
        },

        /**
         * @param commit
         * @param state
         * @param dispatch
         * @returns {Promise<unknown>}
         */
        createAccount({ commit, state, dispatch }) {
            return new Promise((resolve, reject) => {
                dispatch('saveImpression').then(() => {
                    window.grecaptcha.ready(() => {
                        window.grecaptcha.execute(window.recaptchaV3Sitekey, {action:  `${window.app.id}SignUp`})
                            .then(token => {
                                if (token) {
                                    return window.Vue.$http.post(`/accounts/create`, {
                                        signup: state.signup,
                                        account: state.account,
                                        contact: state.contact,
                                        loan: state.loan,
                                        recaptcha: token
                                    }).then(({data}) => {
                                        // Returning customer whose existing loan is Application
                                        // Incomplete: the backend tells us to route them into their
                                        // existing application instead of creating a new account —
                                        // either 'resume' (auto-login link) or 'login' (web funnel
                                        // sends them to sign in first). Navigate there and leave this
                                        // promise unresolved so callers don't run their own
                                        // post-create redirect over the top of it.
                                        if (data && (data.action === 'resume' || data.action === 'login') && data.redirect_url) {
                                            window.location.href = data.redirect_url;
                                            return;
                                        }
                                        resolve(data);
                                    }).catch(error => {
                                        commit('updateErrors', error.response.data.errors);
                                        reject(error);
                                    });
                                } else {
                                    reject(new Error('reCAPTCHA did not return a token. Please try again.'));
                                }
                            });
                    });
                });
            });
        }
    },
    plugins: [vuexLocal.plugin, vuexSsnSessionPlugin]
});
